/**
 * SecureVideoPlayer — Production-grade secure video player component.
 *
 * Security layers implemented:
 *  1. Short-lived signed URL from backend token endpoint (15 min expiry)
 *  2. Auto token refresh 2 min before expiry
 *  3. Custom controls — native controls removed (no browser download button)
 *  4. Right-click context menu blocked
 *  5. Dynamic moving watermark (userId + email + timestamp, repositions every 15s)
 *  6. DevTools detection — pause + warn on detection (desktop only)
 *  7. Tab/visibility change detection — auto-pause
 *  8. Screen recording / external display warning (screen.isExtended)
 *  9. Keyboard shortcut blocking (F12, Ctrl+Shift+I, Ctrl+U, etc.)
 * 10. Drag prevention on video element
 * 11. disablePictureInPicture attribute
 * 12. controlsList="nodownload nofullscreen noremoteplayback"
 * 13. Security events reported to backend audit log
 * 14. CSS overlay prevents easy grab/drag of video
 *
 * Player UX:
 * - Fullscreen works on iOS (webkitEnterFullscreen on video element)
 * - Fullscreen works on Android Chrome and all desktop browsers
 * - Touch tap to show/hide controls; double-tap to toggle fullscreen
 * - Progress bar supports both mouse click and touch
 * - Volume slider hidden on mobile (iOS ignores programmatic volume changes)
 * - Controls auto-hide after 3 s of inactivity
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  requestVideoPlaybackToken,
  reportVideoSecurityEvent,
  type PlaybackTokenResponse,
  type VideoWatermark,
} from "@/api/lms/courses";
import {
  Loader2,
  ShieldAlert,
  ShieldX,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SecureVideoPlayerProps {
  lessonId: string;
  courseId: string;
  chapterId: string;
  className?: string;
}

interface WatermarkPosition {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
}

// ─── Watermark positions (6 quadrants, randomized) ───────────────────────────

const WATERMARK_POSITIONS: WatermarkPosition[] = [
  { top: "8%", left: "5%" },
  { top: "8%", right: "5%" },
  { bottom: "12%", left: "5%" },
  { bottom: "12%", right: "5%" },
  { top: "45%", left: "5%" },
  { top: "45%", right: "5%" },
];

function getRandomPosition(exclude?: number): number {
  let idx: number;
  do {
    idx = Math.floor(Math.random() * WATERMARK_POSITIONS.length);
  } while (idx === exclude && WATERMARK_POSITIONS.length > 1);
  return idx;
}

function formatTime(s: number): string {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

// ─── Device helpers ───────────────────────────────────────────────────────────

/** Returns true on any touch-primary mobile device (phone or tablet). */
function isMobileDevice(): boolean {
  return /Mobi|Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

/** Returns true on iOS Safari / Chrome-on-iOS where the Fullscreen API
 *  is unavailable and we must use webkitEnterFullscreen() on the video element. */
function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

// ─── DevTools detection heuristic (desktop-only) ─────────────────────────────

/** Mobile browsers (especially Chrome on iOS/Android) have a naturally large
 *  gap between outerHeight and innerHeight due to browser UI chrome, which
 *  causes false-positive DevTools alerts. Since DevTools cannot be opened
 *  locally on a mobile device, we skip the check entirely on those platforms. */
function isDevToolsOpen(): boolean {
  if (isMobileDevice()) return false;
  const threshold = 160;
  const widthDiff = window.outerWidth - window.innerWidth;
  const heightDiff = window.outerHeight - window.innerHeight;
  return widthDiff > threshold || heightDiff > threshold;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SecureVideoPlayer({
  lessonId,
  courseId,
  chapterId,
  className,
}: SecureVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  // Token / URL state
  const [tokenData, setTokenData] = useState<PlaybackTokenResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Player state
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Security state
  const [devtoolsWarning, setDevtoolsWarning] = useState(false);
  const [screenRecordingWarning, setScreenRecordingWarning] = useState(false);

  // Watermark state
  const [watermark, setWatermark] = useState<VideoWatermark | null>(null);
  const [wmPosIdx, setWmPosIdx] = useState(0);
  const [wmOpacity, setWmOpacity] = useState(0.55);
  const tokenRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Double-tap detection (mobile fullscreen)
  const lastTapRef = useRef<number>(0);

  // ─── Fetch playback token ────────────────────────────────────────────────────

  const fetchToken = useCallback(async () => {
    setLoading(true);
    setTokenError(null);
    try {
      const data = await requestVideoPlaybackToken(courseId, chapterId, lessonId);
      setTokenData(data);
      setWatermark(data.watermark);

      // Schedule refresh 2 minutes before expiry
      if (tokenRefreshTimer.current) clearTimeout(tokenRefreshTimer.current);
      const refreshIn = Math.max((data.expiresInSeconds - 120) * 1000, 30_000);
      tokenRefreshTimer.current = setTimeout(() => {
        void fetchToken();
      }, refreshIn);
    } catch (err: any) {
      const msg: string = err?.message ?? "Failed to load video. Please refresh.";
      setTokenError(msg);
    } finally {
      setLoading(false);
    }
  }, [lessonId, courseId, chapterId]);

  useEffect(() => {
    void fetchToken();
    return () => {
      if (tokenRefreshTimer.current) clearTimeout(tokenRefreshTimer.current);
    };
  }, [fetchToken]);

  // ─── Video event listeners ───────────────────────────────────────────────────

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (video.buffered.length > 0) {
        setBuffered(video.buffered.end(video.buffered.length - 1));
      }
    };
    const onDurationChange = () => setDuration(video.duration);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("durationchange", onDurationChange);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("durationchange", onDurationChange);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
    };
  }, [tokenData]);

  // ─── Controls auto-hide ──────────────────────────────────────────────────────

  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
  }, []);

  // ─── DevTools detection (desktop only) ──────────────────────────────────────

  useEffect(() => {
    let lastState = false;

    const checkDevTools = () => {
      const open = isDevToolsOpen();
      if (open && !lastState) {
        lastState = true;
        setDevtoolsWarning(true);
        videoRef.current?.pause();
        void reportVideoSecurityEvent(lessonId, courseId, "devtools_opened");
      } else if (!open && lastState) {
        lastState = false;
        setDevtoolsWarning(false);
      }
    };

    const interval = setInterval(checkDevTools, 1000);
    return () => clearInterval(interval);
  }, [lessonId, courseId]);

  // ─── Tab / visibility detection ──────────────────────────────────────────────

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        videoRef.current?.pause();
        void reportVideoSecurityEvent(lessonId, courseId, "visibility_change");
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [lessonId, courseId]);

  // ─── Screen recording detection (screen.isExtended) ─────────────────────────

  useEffect(() => {
    const checkScreen = () => {
      // @ts-ignore – screen.isExtended may not be in all TS libs yet
      if (typeof screen !== "undefined" && (screen as any).isExtended === true) {
        setScreenRecordingWarning(true);
        void reportVideoSecurityEvent(lessonId, courseId, "extended_display_detected");
      } else {
        setScreenRecordingWarning(false);
      }
    };
    checkScreen();
    // @ts-ignore
    screen?.addEventListener?.("change", checkScreen);
    return () => {
      // @ts-ignore
      screen?.removeEventListener?.("change", checkScreen);
    };
  }, [lessonId, courseId]);

  // ─── Keyboard shortcut blocking ──────────────────────────────────────────────

  useEffect(() => {
    const BLOCKED_KEYS = new Set(["F12", "F11"]);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (BLOCKED_KEYS.has(e.key)) {
        e.preventDefault();
        return;
      }
      // Ctrl/Cmd + Shift + I, J, C — DevTools
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && ["i", "j", "c", "I", "J", "C"].includes(e.key)) {
        e.preventDefault();
        return;
      }
      // Ctrl + U (view source), Ctrl + S (save)
      if ((e.ctrlKey || e.metaKey) && ["u", "s", "U", "S"].includes(e.key)) {
        e.preventDefault();
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, []);

  // ─── Right-click block ───────────────────────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const block = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      void reportVideoSecurityEvent(lessonId, courseId, "right_click_attempted");
    };

    container.addEventListener("contextmenu", block);
    return () => container.removeEventListener("contextmenu", block);
  }, [lessonId, courseId]);

  // ─── Watermark position cycling ──────────────────────────────────────────────

  useEffect(() => {
    if (!watermark) return;
    const interval = setInterval(() => {
      setWmPosIdx((prev) => getRandomPosition(prev));
      setWmOpacity(0.4 + Math.random() * 0.25);
    }, 15_000);
    return () => clearInterval(interval);
  }, [watermark]);

  // ─── Fullscreen handling — cross-platform ────────────────────────────────────
  //
  // iOS Safari & Chrome-on-iOS do NOT support the standard Fullscreen API
  // (document.requestFullscreen). Instead, the <video> element exposes a
  // proprietary webkitEnterFullscreen() method that must be called directly.
  // On all other platforms (Android Chrome, desktop Chrome/Firefox/Safari/Edge)
  // we use the standard API on the container element, with webkit prefixed
  // fallbacks for older browsers.

  useEffect(() => {
    // Standard + webkit prefixed fullscreen change (desktop + Android)
    const onFsChange = () => {
      setIsFullscreen(
        !!(document.fullscreenElement ||
          (document as any).webkitFullscreenElement)
      );
    };

    // iOS Safari fires these events on the video element itself
    const onWebkitBeginFs = () => setIsFullscreen(true);
    const onWebkitEndFs = () => setIsFullscreen(false);

    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);

    const video = videoRef.current;
    if (video) {
      video.addEventListener("webkitbeginfullscreen", onWebkitBeginFs);
      video.addEventListener("webkitendfullscreen", onWebkitEndFs);
    }

    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      if (video) {
        video.removeEventListener("webkitbeginfullscreen", onWebkitBeginFs);
        video.removeEventListener("webkitendfullscreen", onWebkitEndFs);
      }
    };
  }, [tokenData]); // re-attach when video element re-renders with new src

  // ─── Player controls ─────────────────────────────────────────────────────────

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  }, []);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
    }
    setVolume(val);
    setMuted(val === 0);
  }, []);

  /** Seek from a clientX position on the progress bar. */
  const seekToClientX = useCallback(
    (clientX: number) => {
      const bar = progressRef.current;
      const video = videoRef.current;
      if (!bar || !video || !duration) return;
      const rect = bar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      video.currentTime = ratio * duration;
    },
    [duration]
  );

  const handleSeekClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      seekToClientX(e.clientX);
    },
    [seekToClientX]
  );

  /** Touch seek — use the first touch point position. */
  const handleSeekTouch = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      e.preventDefault(); // prevent scroll while seeking
      const touch = e.changedTouches[0];
      if (touch) seekToClientX(touch.clientX);
    },
    [seekToClientX]
  );

  /** Toggle fullscreen — works correctly on iOS, Android, and all desktops. */
  const toggleFullscreen = useCallback(() => {
    const video = videoRef.current as any;
    const container = containerRef.current as any;

    if (isIOS()) {
      // iOS: standard Fullscreen API is unavailable; use webkit API on video element
      if (video?.webkitDisplayingFullscreen) {
        video.webkitExitFullscreen?.();
      } else {
        video?.webkitEnterFullscreen?.();
      }
      return;
    }

    // Standard API with webkit fallback — use container for all other platforms
    const fsElement =
      document.fullscreenElement || (document as any).webkitFullscreenElement;

    if (!fsElement) {
      if (container?.requestFullscreen) {
        void container.requestFullscreen();
      } else if (container?.webkitRequestFullscreen) {
        void container.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        void document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        void (document as any).webkitExitFullscreen();
      }
    }
  }, []);

  /** Handle a tap on the video container:
   *  - Single tap  → toggle controls visibility
   *  - Double tap  → toggle fullscreen             */
  const handleContainerTap = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      // Ignore taps that land directly on the controls bar
      const target = e.target as HTMLElement;
      if (target.closest("[data-controls-bar]")) return;

      const now = Date.now();
      const delta = now - lastTapRef.current;
      lastTapRef.current = now;

      if (delta < 300 && delta > 0) {
        // Double-tap → fullscreen
        toggleFullscreen();
      } else {
        // Single tap → show / hide controls
        if (showControls) {
          setShowControls(false);
          if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
        } else {
          showControlsTemporarily();
        }
      }
    },
    [toggleFullscreen, showControls, showControlsTemporarily]
  );

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="aspect-video w-full rounded-2xl border border-border bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-white/60">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Securing video stream…</p>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="aspect-video w-full rounded-2xl border border-border bg-black flex items-center justify-center p-6 text-center">
        <div className="flex flex-col items-center gap-3 text-white/80 max-w-xs">
          <ShieldX className="h-10 w-10 text-red-400" />
          <p className="font-semibold text-sm">Could not load video</p>
          <p className="text-xs text-white/50">{tokenError}</p>
          <button
            type="button"
            onClick={() => void fetchToken()}
            className="mt-2 rounded-lg bg-white/10 px-4 py-2 text-xs font-medium hover:bg-white/20 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const wmPos = WATERMARK_POSITIONS[wmPosIdx];
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPct = duration > 0 ? (buffered / duration) * 100 : 0;
  const mobile = isMobileDevice();

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative overflow-hidden rounded-2xl border border-border bg-black select-none aspect-video group",
        className,
      )}
      onMouseMove={showControlsTemporarily}
      onMouseEnter={showControlsTemporarily}
      onTouchStart={showControlsTemporarily}
      onTouchEnd={handleContainerTap}
      style={{ WebkitUserSelect: "none", userSelect: "none" }}
    >
      {/* ── Video element ── */}
      <video
        ref={videoRef}
        src={tokenData?.signedUrl}
        className="h-full w-full object-contain"
        playsInline
        disablePictureInPicture
        // @ts-ignore
        disableRemotePlayback
        controlsList="nodownload nofullscreen noremoteplayback"
        onDragStart={(e) => e.preventDefault()}
        onClick={togglePlay}
        onDoubleClick={!mobile ? toggleFullscreen : undefined}
      />

      {/* ── Invisible security overlay (blocks drag, prevents right-click grab) ── */}
      <div
        className="absolute inset-0 z-10"
        style={{ pointerEvents: "none" }}
        aria-hidden="true"
      />

      {/* ── Watermark overlay ── */}
      {watermark && (
        <div
          className="absolute z-20 pointer-events-none transition-all duration-1000"
          style={{
            ...wmPos,
            opacity: wmOpacity,
            maxWidth: "55%",
          }}
          aria-hidden="true"
        >
          <p
            className="font-mono text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]"
            style={{
              fontSize: "clamp(9px, 1.1vw, 13px)",
              lineHeight: 1.5,
              textShadow: "0 1px 6px rgba(0,0,0,0.95), 0 0 2px rgba(0,0,0,0.8)",
              letterSpacing: "0.02em",
            }}
          >
            {watermark.email}
            <br />
            {watermark.userId ? `ID:${watermark.userId}` : "Guest Session"} · {new Date(watermark.issuedAt).toLocaleTimeString()}
          </p>
        </div>
      )}

      {/* ── DevTools warning overlay ── */}
      {devtoolsWarning && (
        <div className="absolute inset-0 z-40 bg-black/95 flex flex-col items-center justify-center gap-4 text-white p-6 text-center">
          <ShieldAlert className="h-12 w-12 text-amber-400" />
          <h3 className="text-lg font-bold">Developer Tools Detected</h3>
          <p className="text-sm text-white/70 max-w-sm">
            Video playback is paused while browser developer tools are open.
            Please close DevTools to resume.
          </p>
        </div>
      )}

      {/* ── Screen recording warning ── */}
      {screenRecordingWarning && !devtoolsWarning && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 bg-amber-500/90 text-black text-xs font-semibold px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
          <ShieldAlert className="h-3.5 w-3.5" />
          External display detected. Recording is prohibited.
        </div>
      )}

      {/* ── Custom controls bar ── */}
      <div
        data-controls-bar=""
        className={cn(
          "absolute bottom-0 left-0 right-0 z-30 transition-all duration-300 px-3 pb-2 pt-8 sm:px-4 sm:pb-3",
          "bg-gradient-to-t from-black/80 to-transparent",
          showControls || !playing
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-2 pointer-events-none",
        )}
        onMouseEnter={() => {
          if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
          setShowControls(true);
        }}
        onMouseLeave={showControlsTemporarily}
      >
        {/* Progress bar — supports both mouse click and touch */}
        <div
          ref={progressRef}
          className="relative h-1.5 w-full cursor-pointer mb-2 sm:mb-3 group/pb"
          onClick={handleSeekClick}
          onTouchEnd={handleSeekTouch}
          role="slider"
          aria-label="Video progress"
          aria-valuenow={Math.round(progressPct)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          {/* Track */}
          <div className="absolute inset-0 rounded-full bg-white/20" />
          {/* Buffered */}
          <div
            className="absolute top-0 left-0 h-full rounded-full bg-white/30"
            style={{ width: `${bufferedPct}%` }}
          />
          {/* Played */}
          <div
            className="absolute top-0 left-0 h-full rounded-full bg-primary transition-[width]"
            style={{ width: `${progressPct}%` }}
          />
          {/* Thumb — always visible on mobile for larger tap target, hover-only on desktop */}
          <div
            className={cn(
              "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3.5 w-3.5 rounded-full bg-white shadow transition-opacity",
              mobile ? "opacity-100" : "opacity-0 group-hover/pb:opacity-100",
            )}
            style={{ left: `${progressPct}%` }}
          />
        </div>

        {/* Controls row */}
        <div className="flex items-center gap-2 sm:gap-3 text-white">
          {/* Play / Pause */}
          <button
            type="button"
            id="secure-video-play-pause"
            className="shrink-0 p-1 hover:text-primary transition-colors touch-manipulation"
            onClick={togglePlay}
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
          </button>

          {/* Volume — hidden on mobile (iOS ignores JS volume; Android uses OS controls) */}
          {!mobile && (
            <>
              <button
                type="button"
                className="shrink-0 p-1 hover:text-primary transition-colors"
                onClick={toggleMute}
                aria-label={muted ? "Unmute" : "Mute"}
              >
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-16 h-1 accent-primary cursor-pointer"
                aria-label="Volume"
              />
            </>
          )}

          {/* Time display */}
          <span className="text-xs font-mono text-white/70 flex-1 truncate">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          {/* Fullscreen */}
          <button
            type="button"
            className="shrink-0 p-1 hover:text-primary transition-colors touch-manipulation"
            onClick={(e) => {
              e.stopPropagation(); // prevent triggering container tap handler
              toggleFullscreen();
            }}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* ── Centre play button (when paused) ── */}
      {!playing && !loading && tokenData && !devtoolsWarning && (
        <button
          type="button"
          className="absolute inset-0 z-20 flex items-center justify-center"
          onClick={togglePlay}
          aria-label="Play video"
        >
          <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-black/50 flex items-center justify-center border border-white/20 hover:bg-primary/80 active:scale-95 transition-all duration-150">
            <Play className="h-6 w-6 sm:h-7 sm:w-7 text-white ml-1" />
          </div>
        </button>
      )}
    </div>
  );
}
